import { useState, useEffect, useCallback, useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import apiClient from '../../services/api/apiClient';
import IAlertType from '../../utils/types/props/IAlertType';
import AlertComponent from '../../components/utility/AlertComponent';
import { Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { IRole } from '../../utils/types/IRole';
import RoleSelect from '../../components/select/RoleSelect';
import { Permission } from '../../utils/types/Permission';
import PermissionGrid from '../../components/grid/PermissionGrid';
import Swal from 'sweetalert2';
import { formatAxiosError } from '../../utils/formatAxiosError';
import SkeletonLoader from '../../components/loader/SkeletonLoader';
import PermissionTypeSelect from '../../components/select/PermissionTypeSelect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';
import { toast } from 'sonner';

const PermissionPage = () => {
    const queryClient = useQueryClient();

    // ---

    const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState<string | null>(null);

    const [permissionDTOs, setPermissionDTOs] = useState<Permission[]>([]);

    // ---

    const {
        data: roles = [],
        isLoading: isLoadingRoles,
        refetch: refetchRoles,
        isError: isRolesError,
        error: rolesError,
    } = useQuery<IRole[]>({
        queryKey: ['roles'],
        queryFn: async () => {
            const res = await apiClient.get('/api/role');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des rôles' },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: permissions = [],
        isLoading: isLoadingPermissions,
        refetch: refetchPermissions,
        isError: isPermissionsError,
        error: permissionsError,
    } = useQuery<Permission[]>({
        queryKey: [
            'permissions',
            {
                idRole: selectedRoleId,
                typeRessource: selectedType,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/permission/byRole', {
                params: {
                    idRole: selectedRoleId,
                    typeRessource: selectedType,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement des permissions' },
        enabled: Boolean(selectedRoleId),
    });

    // ---

    const { mutate, isPending: isSubmittingOperation } = useMutation({
        mutationFn: () =>
            apiClient.post('/api/permission/update-permissions', {
                idRole: selectedRoleId,
                permissions: permissionDTOs,
            }),
        onSuccess: (response) => {
            if (response.data.success === true) {
                Swal.fire({
                    text: 'Opération effectuée avec succès.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    handleLoadPermissions();
                });
            } else {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: "Erreur lors l'enregistrement de l'opération. " + response.data.message,
                    },
                ]);
            }
        },
        onError: (err) => {
            addAlert('danger', formatAxiosError(err)?.message ?? "Erreur lors de l'enregistrement de l'opération.");
        },
    });

    // ---

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    // ---

    useEffect(() => {
        if (roles.length === 1) {
            setSelectedRoleId(roles[0].id);
        }
    }, [roles]);

    useEffect(() => {
        setPermissionDTOs(permissions);
    }, [permissions]);

    // ---

    const handleLoadPermissions = useCallback(async () => {
        if (!selectedRoleId) return;

        await queryClient.invalidateQueries({
            queryKey: [
                'permissions',
                {
                    idRole: selectedRoleId,
                    typeRessource: selectedType,
                },
            ],
        });

        const { data } = await refetchPermissions();

        if (data) {
            setPermissionDTOs(data);
        }
    }, [selectedRoleId, selectedType, refetchPermissions, queryClient, setPermissionDTOs]);

    const handlePermissionUpdated = (updatedPermission: Permission) => {
        setPermissionDTOs((prev) => {
            const newPermissions = prev.map((p) => (p.idRessource === updatedPermission.idRessource ? { ...updatedPermission } : p));
            return newPermissions;
        });
    };

    const submitForm = () => {
        if (!selectedRoleId) {
            Swal.fire({
                text: 'Rôle incorrect.',
                icon: 'error',
            });
            return;
        }

        if (permissionDTOs.length === 0) {
            toast("Vous n'avez configuré aucune permission.");
            return;
        }

        mutate();
    };

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isRolesError, error: rolesError, title: 'Erreur lors du chargement des rôles', refetch: refetchRoles },
            { isError: isPermissionsError, error: permissionsError, title: 'Erreur lors du chargement des permissions', refetch: refetchPermissions },
        ],
        [isRolesError, rolesError, refetchRoles, isPermissionsError, permissionsError, refetchPermissions],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Permissions :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Listing</div>
                            <h2 className="page-title">Permissions</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
                    <QueryErrorAlerts queries={queryErrors} />

                    <div className="card">
                        <div className="accordion accordion-flush" id="accordion-flush">
                            <div className="accordion-item">
                                <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-flush" aria-expanded="true">
                                    <div className="accordion-header-text">
                                        <h4>Filtre</h4>
                                    </div>
                                    <div className="accordion-header-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                            <path d="M6 9l6 6l6 -6"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="collapse-1-flush" className="accordion-collapse collapse show" data-bs-parent="#accordion-flush">
                                    <div className="accordion-body">
                                        <div>
                                            <div className="row">
                                                <div className="col-sm-3 form-group">
                                                    <label>Rôle *</label>
                                                    <RoleSelect roles={roles} isRolesLoading={isLoadingRoles} selectedRoleId={selectedRoleId} setSelectedRoleId={setSelectedRoleId} />
                                                </div>

                                                <div className="col-sm-2 form-group">
                                                    <label>Type</label>
                                                    <PermissionTypeSelect selectedType={selectedType} setSelectedType={setSelectedType} />
                                                </div>
                                            </div>

                                            <div className="form-group mt-2">
                                                <button type="button" className="btn btn-default" disabled={isLoadingPermissions} onClick={handleLoadPermissions}>
                                                    {isLoadingPermissions ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                            &nbsp; Générer
                                                        </>
                                                    ) : (
                                                        'Générer'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2">
                        {isLoadingPermissions ? (
                            <>
                                <p className="text-center">En cours de chargement</p>
                                <SkeletonLoader />
                            </>
                        ) : permissionDTOs.length > 0 ? (
                            <>
                                <PermissionGrid permissions={permissionDTOs} handlePermissionUpdated={handlePermissionUpdated} />

                                <div className="text-end mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={() => {
                                            submitForm();
                                        }}
                                        disabled={permissionDTOs.length === 0 || isSubmittingOperation}
                                    >
                                        {isSubmittingOperation ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                &nbsp; Valider
                                            </>
                                        ) : (
                                            `Valider`
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="alert alert-warning" role="alert">
                                <div className="alert-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                        <path d="M12 9v4"></path>
                                        <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                        <path d="M12 16h.01"></path>
                                    </svg>
                                </div>
                                Aucune donnée.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

PermissionPage.Layout = DefaultLayout;

export default PermissionPage;
